/** @module Ingredient */
/* eslint-disable */

import { sort } from 'ramda';

import { INGREDIENTS, INGREDIENTS as INGREDIENT_NAME } from '@/constants/ingredients';
import { kojicPerCapsule } from '@/models/core/kojicConcentration';

const CAPSULE_CONCENTRATION = {
  [INGREDIENT_NAME.ALOE]: 5.4,
  [INGREDIENT_NAME.ARBUTIN]: 3.0,
  [INGREDIENT_NAME.ASCORBIC]: 7.0,
  [INGREDIENT_NAME.AZELAIC]: 5.4,
  [INGREDIENT_NAME.DMAE]: 2.5,
  [INGREDIENT_NAME.DPANTHENOL]: 5.0,
  [INGREDIENT_NAME.FERULIC]: 1.0,
  [INGREDIENT_NAME.GLYCOLIC]: 7.0,
  [INGREDIENT_NAME.ISO]: 2.0,
  // NOTE: kojic is NOT read from here anymore — the Ingredient factory routes it
  // through kojicPerCapsule(countryCode) (1% non-US/CA, 2% US/CA). Kept only so
  // the table stays complete; this 2.0 is dead and must not be treated as live.
  [INGREDIENT_NAME.KOJIC]: 2.0,
  [INGREDIENT_NAME.LACTO]: 3.5,
  [INGREDIENT_NAME.MADECA]: 1.0,
  [INGREDIENT_NAME.NIACINAMIDE]: 4.0,
  [INGREDIENT_NAME.PHYTIC]: 2.0,
  [INGREDIENT_NAME.RETINOL]: 0.2,
  [INGREDIENT_NAME.RUTIN]: 3.0,
  [INGREDIENT_NAME.SALICYLIC]: 2.0,
  [INGREDIENT_NAME.SOD]: 2.0,
  [INGREDIENT_NAME.ZINC]: 2.5,
  [INGREDIENT_NAME.TRANEXAMIC]: 4.0,
  [INGREDIENT_NAME.RETINAL]: 0.2
};

const INGREDIENT_SORT_INDEX = {
  [INGREDIENTS.ALOE]: 4,
  [INGREDIENTS.ARBUTIN]: 8,
  [INGREDIENTS.ASCORBIC]: 2,
  [INGREDIENTS.AZELAIC]: 3,
  [INGREDIENTS.DMAE]: 2,
  [INGREDIENTS.DPANTHENOL]: 4,
  [INGREDIENTS.FERULIC]: 1,
  [INGREDIENTS.GLYCOLIC]: 6,
  [INGREDIENTS.ISO]: 2,
  [INGREDIENTS.KOJIC]: 8,
  [INGREDIENTS.LACTO]: 7,
  [INGREDIENTS.MADECA]: 2,
  [INGREDIENTS.NIACINAMIDE]: 3,
  [INGREDIENTS.PHYTIC]: 6,
  [INGREDIENTS.RETINOL]: 2,
  [INGREDIENTS.RUTIN]: 3,
  [INGREDIENTS.SALICYLIC]: 6,
  [INGREDIENTS.SOD]: 1,
  [INGREDIENTS.ZINC]: 5,
  [INGREDIENTS.RETINAL]: 2,
  [INGREDIENTS.TRANEXAMIC]: 8
};

/**
 * A serum ingredient.
 *
 * @typedef {Object} Ingredient
 * @property {string} name
 * @property {number} nbOfCapsules
 * @property {number} totalConcentration
 */

/**
 *Ingredient constructor.
 *
 * @function
 * @param {string} name
 * @param {number} nbOfCapsules
 * @param {string} [countryCode] - optional delivery country; omit for legacy 2.0 kojic behaviour
 * @return {Ingredient}
 */
export const Ingredient = (name, nbOfCapsules, countryCode) => {
  const perCapsule =
    name === INGREDIENT_NAME.KOJIC ? kojicPerCapsule(countryCode) : CAPSULE_CONCENTRATION[name];
  const totalConcentration = nbOfCapsules * perCapsule;

  return { name, nbOfCapsules, totalConcentration };
};

/**
 * Get ingredient name.
 *
 * @function
 * @param {Ingredient} ingredient
 * @return {string}
 */
export const getIngredientName = ingredient => ingredient.name;

/**
 * Get total concentration of ingredient (all capsules).
 *
 * @function
 * @param {Ingredient} ingredient
 * @return {number}
 */
export const getIngredientTotalConcentration = ingredient => ingredient.totalConcentration;

/**
 * Sort a list of ingredients by ingredient-specific sort order.
 *
 * @function
 * @param {Ingredient[]} ingredients
 * @return {Ingredient[]}
 */
export const sortIngredients = ingredients => {
  return sort((a, b) => {
    const nameA = getIngredientName(a);
    const nameB = getIngredientName(b);

    return INGREDIENT_SORT_INDEX[nameA] - INGREDIENT_SORT_INDEX[nameB];
  }, ingredients);
};

/**
 * Check for equality two ingredients.
 *
 * @function
 * @param {Ingredient} ingredientA
 * @param {Ingredient} ingredientB
 * @return {boolean}
 */
export const isIngredientEqual = (ingredientA, ingredientB) => {
  return getIngredientName(ingredientA) === getIngredientName(ingredientB);
};
